import { FC, useEffect } from "react";
import { Controller } from "react-hook-form";
import useI18n from "../../../../hooks/useTranslations";
import { AdminCommonSteelVersion } from "../../../../http/types/commonSteelVersions";
import { AdminGlobalSteelVersion } from "../../../../http/types/globalSteelVersions";
import Form from "../../../ui/Form";
import FormField from "../../../ui/FormField";
import FormFieldsContainer from "../../../ui/FormFieldsContainer";
import useImageUploader from "../../../ui/ImageUploader";
import Input from "../../../ui/Input";
import MessageBox from "../../../ui/MessageBox";
import Numeric from "../../../ui/Numeric";
import Textarea from "../../../ui/Textarea";
import TranslatableField from "../../../ui/TranslatableField";
import VisibilityScope, { VisibilityScopeValue } from "../../../ui/VisibilityScope";

export interface UpsertSteelVersionForm {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  description: string | null;
  descriptionIt: string | null;
  descriptionDe: string | null;
  descriptionEs: string | null;
  descriptionFr: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
}

export interface SteelVersionDetailsProps {
  steelVersion?: AdminCommonSteelVersion | AdminGlobalSteelVersion;
  isDisabled?: boolean;
  isLoading?: boolean;
  isGlobalSettings: boolean;
  isSubmitting: boolean;
  onSubmit: (
    data: UpsertSteelVersionForm,
    newImage: File | undefined,
    removeImage: boolean,
    onSave: () => number,
  ) => Promise<void>;
}

const SteelVersionDetails: FC<SteelVersionDetailsProps> = ({
  steelVersion,
  isDisabled,
  isLoading = false,
  isGlobalSettings,
  isSubmitting,
  onSubmit,
}: SteelVersionDetailsProps) => {
  const i18n = useI18n();

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: steelVersion?.image,
    isLoading: isLoading,
    footer: <MessageBox text={i18n.translation.steelVersions.warnings.editWarning} type="warning" />,
  });

  const defaultValues = {
    name: steelVersion?.name,
    nameIt: steelVersion?.nameIt ?? null,
    nameDe: steelVersion?.nameDe ?? null,
    nameEs: steelVersion?.nameEs ?? null,
    nameFr: steelVersion?.nameFr ?? null,
    order: steelVersion != undefined && "order" in steelVersion ? steelVersion.order : 50,
    description: steelVersion?.description,
    descriptionIt: steelVersion?.descriptionIt ?? null,
    descriptionDe: steelVersion?.descriptionDe ?? null,
    descriptionEs: steelVersion?.descriptionEs ?? null,
    descriptionFr: steelVersion?.descriptionFr ?? null,
    visibility: steelVersion != undefined && "visibility" in steelVersion ? steelVersion.visibility : undefined,
  };

  return (
    <Form<UpsertSteelVersionForm>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || isDisabled}
      onSubmit={() => async (body) => onSubmit(body, newImage, removeImage, onSave)}
    >
      {({ register, formState: { errors }, control, setValue, reset, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [steelVersion]);

        useEffect(() => {
          register("nameIt");
          register("nameDe");
          register("nameEs");
          register("nameFr");
          register("descriptionIt");
          register("descriptionDe");
          register("descriptionEs");
          register("descriptionFr");
        }, [register]);

        const values = watch();

        return (
          <FormFieldsContainer imageUploader={component}>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isOptional, isInvalid }) => {
                return (
                  <TranslatableField
                    field="name"
                    formValues={values}
                    setFormValue={setValue}
                    dialogTitle={i18n.translation.common.name}
                  >
                    <Input
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={isDisabled}
                      {...register("name", {
                        required: !isOptional,
                      })}
                    />
                  </TranslatableField>
                );
              }}
            </FormField>
            <MessageBox text={i18n.translation.steelVersions.warnings.editWarning} type="warning" />

            <FormField label={i18n.translation.common.description} error={errors.description}>
              {({ labelId, isOptional, isInvalid }) => {
                return (
                  <TranslatableField
                    field="description"
                    formValues={values}
                    setFormValue={setValue}
                    dialogTitle={i18n.translation.common.description}
                    dialogType="textarea"
                  >
                    <Textarea
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={isDisabled}
                      {...register("description", {
                        required: !isOptional,
                      })}
                    />
                  </TranslatableField>
                );
              }}
            </FormField>
            <MessageBox text={i18n.translation.steelVersions.warnings.editWarning} type="warning" />

            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error} hidden={isGlobalSettings}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isDisabled}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default SteelVersionDetails;
